import { ref, toValue, type MaybeRef } from "vue";
import type { ProfileRole } from "@/api/model";

export interface ProfileOption {
  id: number;
  name: string;
  email: string;
  /** Guardian's email for managed children (no login of their own). */
  sharedEmail?: string | null;
  roles?: ProfileRole[];
  associatedContactId?: number | null;
}

export interface FetchProfilesParams {
  /**
   * Filter profiles by role on the server side
   * Use ProfileRole constants: ProfileRole.player, ProfileRole.contact, ProfileRole.coach, ProfileRole.admin
   */
  role?: string;
  /**
   * Server-side search by name, email, or team name
   */
  search?: string;
  /**
   * Maximum number of profiles to fetch
   */
  limit?: number;
  /**
   * Offset for paginated requests. Defaults to 0.
   */
  offset?: number;
  /**
   * Exclude specific profile IDs (client-side filtering)
   */
  excludeIds?: number[];
}

/**
 * Composable for fetching organization profiles.
 * Provides a flexible fetch function that you call with filter parameters.
 * Does not auto-load or manage state - you control when and how to fetch.
 */
export function useOrganizationProfiles(orgId: MaybeRef<number>) {
  const isLoading = ref(false);

  /**
   * Fetch profiles from an organization with optional filtering.
   * Returns the profiles array directly - you manage the state.
   */
  async function fetchProfiles(
    params: FetchProfilesParams = {}
  ): Promise<ProfileOption[]> {
    const organizationId = toValue(orgId);
    const { role, search, limit = 1000, offset = 0, excludeIds = [] } = params;

    if (!organizationId) {
      return [];
    }

    isLoading.value = true;

    try {
      const { adminV1OrganizationsProfilesList } = await import(
        "@/api/admin-profiles/admin-profiles"
      );

      const response = await adminV1OrganizationsProfilesList(organizationId, {
        limit,
        offset,
        is_active: true,
        ...(role && { role }),
        ...(search && { search }),
      });

      if (response?.results) {
        let filteredProfiles = response.results;

        // Apply ID exclusion filter (client-side)
        if (excludeIds.length > 0) {
          filteredProfiles = filteredProfiles.filter(
            (p) => !excludeIds.includes(p.id)
          );
        }

        return filteredProfiles.map((p) => {
          const option: ProfileOption = {
            id: p.id,
            name: p.name || "N/A",
            email: p.email || "",
            roles: p.roles as ProfileRole[] | undefined,
          };

          if (p.shared_email) {
            option.sharedEmail = p.shared_email;
          }

          if ("associated_contact_id" in p) {
            option.associatedContactId = p.associated_contact_id ?? null;
          }

          return option;
        });
      }

      return [];
    } catch (err) {
      console.error("Error fetching profiles:", err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  return {
    isLoading,
    fetchProfiles,
  };
}
